import { useState, useCallback, useRef } from 'react';

const useCartLogic = (tipo, selectedWholesalePrice) => {
  const [cart, setCart] = useState([]);
  const searchInputRef = useRef(null);

  // Devuelve el id de la combinación si el producto pertenece a una, si no null
  const getCombinacionId = useCallback((product) => {
    if (product.combinaciones && product.combinaciones.length > 0) {
      return product.combinaciones[0].id; // Solo una combinación por producto
    }
    return null;
  }, []);

  const getPrice = useCallback((product) => {
    // Si el producto pertenece a una combinación, sumar cantidades de todos los productos de esa combinación en el carrito
    const combinacionId = getCombinacionId(product);
    if (combinacionId) {
      // Buscar todos los productos del carrito que pertenezcan a la misma combinación
      const productosEnCombinacion = cart.filter(
        (p) => getCombinacionId(p) === combinacionId
      );
      // Sumar cantidades
      const sumaCantidad = productosEnCombinacion.reduce(
        (acc, p) => acc + (p.quantity || 0),
        0
      );
      // Tomar el bulto del producto base de la combinación (puedes ajustar si quieres tomar el mayor o menor)
      const cantidadBultos = product.cantidad_bultos || 0;
      if (cantidadBultos > 0 && sumaCantidad >= cantidadBultos) {
        // Aplica precio mayorista A
        const mayoristaA = product?.precios?.[0]?.precio_mayorista_a;
        if (mayoristaA !== undefined && mayoristaA !== null && !isNaN(mayoristaA) && Number(mayoristaA) > 0) {
          return mayoristaA;
        }
      }
      // Si no, sigue con la lógica normal
    }

    // Si la venta es mayorista, usa el precio mayorista seleccionado si existe, si no el precio base
    if (tipo === "mayorista") {
      const mayorista = product?.precios?.[0]?.[selectedWholesalePrice];
      const base = product?.precios?.[0]?.precio;

      // Si el precio mayorista es válido y mayor a 0, úsalo
      if (mayorista !== undefined && mayorista !== null && !isNaN(mayorista) && Number(mayorista) > 0) {
        return mayorista;
      }
      // Si el precio base es válido y mayor a 0, úsalo
      if (base !== undefined && base !== null && !isNaN(base) && Number(base) > 0) {
        return base;
      }
      // Si ninguno es válido, muestra N/A
      return "N/A";
    }

    // Lógica exclusiva para minorista (bultos/pallets)
    const quantity = product.quantity || 0;
    const unidadesPorBulto = product.cantidad_bultos || 0; // Si es 0, no hay bultos
    const unidadesPorPalet = product.cantidad_pallets || 0; // Si es 0, no hay pallets

    // Si no hay bultos ni pallets, usa el precio general
    if (unidadesPorBulto === 0 && unidadesPorPalet === 0) {
      const base = product?.precios?.[0]?.precio;
      return (base !== undefined && base !== null && !isNaN(base) && Number(base) > 0) ? base : "N/A";
    }

    const cantidadUnidadesBulto = unidadesPorBulto; // Un bulto en unidades
    const cantidadUnidadesMayoristaB = cantidadUnidadesBulto * 10; // 10 bultos en unidades
    const cantidadUnidadesPalet = unidadesPorPalet > 0 ? unidadesPorBulto * unidadesPorPalet : null; // Solo si hay pallets

    // Si hay cantidad_pallets y la cantidad en unidades es igual o mayor a un palet, usar precio mayorista C
    if (cantidadUnidadesPalet !== null && quantity >= cantidadUnidadesPalet) {
      const mayoristaC = product?.precios?.[0]?.precio_mayorista_c;
      const base = product?.precios?.[0]?.precio;
      return (mayoristaC !== undefined && mayoristaC !== null && !isNaN(mayoristaC) && Number(mayoristaC) > 0)
        ? mayoristaC
        : (base !== undefined && base !== null && !isNaN(base) && Number(base) > 0 ? base : "N/A");
    }

    // Si la cantidad en unidades es igual o mayor a 10 bultos pero menor que un palet (si existe), usar precio mayorista B
    if (quantity >= cantidadUnidadesMayoristaB && (cantidadUnidadesPalet === null || quantity < cantidadUnidadesPalet)) {
      const mayoristaB = product?.precios?.[0]?.precio_mayorista_b;
      const base = product?.precios?.[0]?.precio;
      return (mayoristaB !== undefined && mayoristaB !== null && !isNaN(mayoristaB) && Number(mayoristaB) > 0)
        ? mayoristaB
        : (base !== undefined && base !== null && !isNaN(base) && Number(base) > 0 ? base : "N/A");
    }

    // Si la cantidad en unidades es igual o mayor a un bulto pero menor a 10 bultos, usar precio mayorista A
    if (quantity >= cantidadUnidadesBulto && quantity < cantidadUnidadesMayoristaB) {
      const mayoristaA = product?.precios?.[0]?.precio_mayorista_a;
      const base = product?.precios?.[0]?.precio;
      return (mayoristaA !== undefined && mayoristaA !== null && !isNaN(mayoristaA) && Number(mayoristaA) > 0)
        ? mayoristaA
        : (base !== undefined && base !== null && !isNaN(base) && Number(base) > 0 ? base : "N/A");
    }

    // Si la cantidad es menor a un bulto o no hay configuración de bultos, usar el precio general
    const base = product?.precios?.[0]?.precio;

    return (base !== undefined && base !== null && !isNaN(base) && Number(base) > 0) ? base : "N/A";
  }, [cart, tipo, selectedWholesalePrice, getCombinacionId]);

  const isDiscounted = useCallback((product) => product.tieneDescuento, []);
  
  const getDiscountedPrice = useCallback((product) => {
    if (isDiscounted(product)) {
      return parseFloat(product.precioFinal.toFixed(2));
    }
    return getPrice(product);
  }, [isDiscounted, getPrice]);

  const addToCart = useCallback((product, caja, setSearchTerm) => {
    if (!caja) {
      alert('No puedes realizar ventas sin abrir una caja');
      return;
    }

    setCart((prevCart) => {
      const existingProduct = prevCart.find((p) => p.id === product.id);

      if (existingProduct) {
        if (existingProduct.quantity < product.stock_unidades) {
          setSearchTerm("");
          // Reenfoque el input después de un pequeño delay
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }, 100);
          return prevCart.map((p) =>
            p.id === product.id ? { ...p, quantity: p.quantity + 1 } : p
          );
        } else {
          alert('No puedes agregar más unidades de las disponibles en stock.');
          return prevCart; // <-- IMPORTANTE: retorna aquí
        }
      } else {
        if (product.stock_unidades > 0) {
          setSearchTerm("");
          // Reenfoque el input después de un pequeño delay
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }, 100);
          return [...prevCart, { ...product, quantity: 1 }];
        } else {
          alert('Este producto está fuera de stock.');
          return prevCart; // <-- IMPORTANTE: retorna aquí
        }
      }
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart((prevCart) => prevCart.filter((product) => product.id !== productId));
  }, []);

  const increaseQuantity = useCallback((productId) => {
    setCart((prevCart) =>
      prevCart.map((product) => {
        if (product.id === productId) {
          // Asegurar que quantity sea un número válido
          const currentQuantity = parseFloat(product.quantity) || 0;
          const newQuantity = parseFloat((currentQuantity + 1).toFixed(3));
          
          if (newQuantity <= product.stock_unidades) {
            return { ...product, quantity: newQuantity };
          } else {
            alert('No puedes agregar más unidades de las disponibles en stock.');
          }
        }
        return product;
      })
    );
  }, []);

  const decreaseQuantity = useCallback((productId) => {
    setCart((prevCart) =>
      prevCart.map((product) => {
        if (product.id === productId) {
          // Asegurar que quantity sea un número válido
          const currentQuantity = parseFloat(product.quantity) || 0;
          
          if (currentQuantity > 1) {
            const newQuantity = parseFloat((currentQuantity - 1).toFixed(3));
            return { ...product, quantity: newQuantity };
          }
        }
        return product;
      })
    );
  }, []);

  const handleQuantityChange = useCallback((productId, value) => {
    setCart((prevCart) =>
      prevCart.map((product) => {
        if (product.id === productId) {
          // Si el valor está vacío, establecer como 0
          let newQuantity = value === "" ? 0 : parseFloat(value);
          
          // Validar que sea un número válido
          if (isNaN(newQuantity) || newQuantity < 0) {
            newQuantity = 0;
          }
          
          // Validar contra el stock
          if (newQuantity > product.stock_unidades) {
            alert("No hay suficiente stock disponible.");
            newQuantity = product.stock_unidades;
          }
          
          return { ...product, quantity: newQuantity };
        }
        return product;
      })
    );
  }, []);

  const calculateTotal = useCallback(() => {
    const total = cart.reduce((acc, product) => acc + getDiscountedPrice(product) * product.quantity, 0);
    return parseFloat(total.toFixed(2));
  }, [cart, getDiscountedPrice]);

  return {
    cart,
    setCart,
    getPrice,
    getDiscountedPrice,
    addToCart,
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
    handleQuantityChange,
    calculateTotal,
    searchInputRef
  };
};

export default useCartLogic;
